var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { filterFalse, pluralize, willNeverHappen } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { validators } from "../Logic/Validators";
export class AdminInviteUserFormModel {
    constructor(opts) {
        this.usersToInvite = [];
        this.organizationsManager = opts.container.organizationsManager;
        this.authManager = opts.container.authManager;
        this.bannerUIManager = opts.container.bannerUIManager;
        this.onDone = opts.onDone;
        for (let i = 0; i < 3; i++) {
            this.addBlankUser();
        }
    }
    get canAddAnother() {
        return this.usersToInvite.length < 10;
    }
    get isResearcher() {
        return this.authManager.userType === UserType.EXTERNAL_RESEARCHER;
    }
    get isServiceProvider() {
        return this.authManager.userType === UserType.SERVICE_PROVIDER;
    }
    get isHealthcareOrg() {
        return this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION;
    }
    get researcherOrUserText() {
        return {
            label: this.authManager.isDataUser ? "User" : "Researcher",
            tooltip: this.authManager.isDataUser
                ? "Every user added to your organization on HealthEx is a user. Users can create projects and recruit patients."
                : "Every colleague added to your organization on HealthEx is a researcher. Researchers can create studies and recruit patients for research efforts.",
        };
    }
    get emailPlaceholder() {
        switch (this.authManager.userType) {
            case UserType.HEALTHCARE_ORGANIZATION:
                return "ex. name@healthcareorganization.com";
            case UserType.EXTERNAL_RESEARCHER:
                return "ex. name@researcher.com";
            case UserType.SERVICE_PROVIDER:
                return "ex. name@serviceprovider.com";
            case UserType.PATIENT:
            case UserType.PATIENT_READ_ONLY:
            case UserType.SCREENSHOT_USER:
            case null:
                return "";
        }
        return willNeverHappen(this.authManager.userType);
    }
    addAnother() {
        this.addBlankUser();
    }
    addBlankUser() {
        this.usersToInvite.push({
            email: "",
            isITAdmin: false,
            isResearchAdmin: false,
            isResearcher: this.isResearcher,
            isProvider: false,
            error: null,
        });
    }
    cancel() {
        this.onDone();
    }
    get inviteUsersIsDisabled() {
        return !this.usersToInvite.some((user) => user.email.trim().length !== 0 && (user.isITAdmin || user.isResearchAdmin || user.isResearcher || user.isProvider));
    }
    async inviteUsers() {
        var _a;
        const organization = this.organizationsManager.myOrganization.value;
        if (organization === NOT_LOADED || !organization)
            return;
        const existingEmails = new Set(((_a = organization.users) !== null && _a !== void 0 ? _a : []).map((user) => user.email));
        this.usersToInvite.forEach((user) => (user.error = null));
        const actualUsersToInvite = this.usersToInvite.filter((user) => user.email.trim().length > 0);
        const { validEmail } = validators;
        actualUsersToInvite.forEach((user) => {
            const noRoleAssigned = !user.isITAdmin && !user.isResearchAdmin && !user.isResearcher && !user.isProvider;
            if (!validEmail(user.email)) {
                if (noRoleAssigned) {
                    user.error = "Enter a valid email address and assign a role";
                }
                else {
                    user.error = "Enter a valid email address";
                }
            }
            else if (noRoleAssigned) {
                user.error = "Each colleague must be assigned a role";
            }
            else if (existingEmails.has(user.email)) {
                user.error = `${user.email} is already part of your organization on HealthEx`;
            }
        });
        if (actualUsersToInvite.some((user) => !!user.error)) {
            return;
        }
        await Promise.all(actualUsersToInvite.map(async (user) => {
            const userId = await this.organizationsManager.inviteUser(organization.id, {
                type: organization.type,
                email: user.email,
                permissions: filterFalse([
                    user.isITAdmin && UserPermissions.ORG_ADMIN,
                    user.isResearchAdmin && UserPermissions.RESEARCH_ADMIN,
                    user.isResearcher && UserPermissions.RESEARCHER,
                    user.isProvider && UserPermissions.PROVIDER,
                ]),
            });
        }));
        if (actualUsersToInvite.length > 0) {
            this.bannerUIManager.displayBanner({
                text: `${actualUsersToInvite.length} ${pluralize(actualUsersToInvite.length, "user", "users")} successfully invited`,
            });
        }
        this.onDone();
    }
    get showOrgInvite() {
        const organization = this.organizationsManager.myOrganization.value;
        if (organization === NOT_LOADED || !organization)
            return false;
        return !!(organization === null || organization === void 0 ? void 0 : organization.ssoEnabled);
    }
    get orgInvitePath() {
        return `/#/welcome/org/${this.authManager.orgId}`;
    }
    copyInviteLinkDone() {
        this.bannerUIManager.displayBanner({ text: "Link copied to clipboard" });
    }
}
__decorate([
    observable
], AdminInviteUserFormModel.prototype, "usersToInvite", void 0);
__decorate([
    computed
], AdminInviteUserFormModel.prototype, "canAddAnother", null);
__decorate([
    computed
], AdminInviteUserFormModel.prototype, "isResearcher", null);
__decorate([
    computed
], AdminInviteUserFormModel.prototype, "isServiceProvider", null);
__decorate([
    computed
], AdminInviteUserFormModel.prototype, "isHealthcareOrg", null);
__decorate([
    computed
], AdminInviteUserFormModel.prototype, "researcherOrUserText", null);
__decorate([
    computed
], AdminInviteUserFormModel.prototype, "emailPlaceholder", null);
__decorate([
    computed
], AdminInviteUserFormModel.prototype, "inviteUsersIsDisabled", null);
__decorate([
    computed
], AdminInviteUserFormModel.prototype, "showOrgInvite", null);
__decorate([
    computed
], AdminInviteUserFormModel.prototype, "orgInvitePath", null);
